// React Query drains for the command palette search.
//
// Four drain types:
//   - Register drain: managed contracts, up to 5 pages / 1 000 items. Enabled
//     when the palette is open and the user has the 'manage' area.
//   - Drafts drain: draft contracts, up to 3 pages / 600 items. Enabled when
//     open and user has 'generate'.
//   - Record drains (×8 parallel, useQueries): one query per TAB_LEVEL_TYPE,
//     per-contract. Gated additionally on: contract scope, a non-null
//     contractId, AND at least one typed character — record drains start on
//     the first keystroke so opening the palette fires at most 2 requests.
//   - Org-record drains (×8 parallel, useQueries): the global-scope mirror of
//     the record drains, one query per TAB_LEVEL_TYPE across the whole
//     organisation via trackingApi.orgWideList. Gated on: global scope, the
//     'manage' area (org-wide routes are manage-area-only server-side), AND
//     the same first-keystroke rule. Wider per-type caps (3 pages / 200 items)
//     since they cover the whole register rather than one contract.
//
// Typing fires zero additional network requests; filtering is a useMemo in the
// controller. No input debounce (nothing hits the network per keystroke).
//
// Requests are NOT aborted on close — apiRequest does not consume the
// AbortSignal. Completing "stale" drains simply fill their cache entries.
//
// Flatten is scope-exclusive: contract-scope records come only from
// recordQueries, global-scope records come only from orgRecordQueries (and
// only while the manage area still holds — see below). This keeps a scope
// switch, or a mid-session area revocation, from leaking the other scope's
// cached data into view even though React Query keeps disabled queries'
// cached data around.

import { useQuery, useQueries } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import {
  managedContractsApi,
  contractsApi,
  trackingApi,
  financeApi,
} from "../../services/api";
import type { ManagedContract, Contract, TrackingItem } from "../../types";
import type { OrgWideInvoice } from "../../types/finance";
import { TAB_LEVEL_TYPES } from "../../types";

// ─── Constants ────────────────────────────────────────────────────────────────

// Maximum pages/items per drain to bound memory and request count.
const REGISTER_MAX_PAGES = 5;
const REGISTER_PAGE_SIZE = 200; // 5 × 200 = 1 000 items
const DRAFTS_MAX_PAGES = 3;
const DRAFTS_PAGE_SIZE = 200; // 3 × 200 = 600 items
const RECORDS_MAX_PAGES = 3;
const RECORDS_PAGE_SIZE = 100; // 3 × 100 = 300 items per type
const ORG_RECORDS_MAX_PAGES = 3;
const ORG_RECORDS_PAGE_SIZE = 200; // 3 × 200 = 600 items per type

const PALETTE_STALE_TIME = 60_000; // 60 s — warm the cache for the session

// ─── Drain helpers ────────────────────────────────────────────────────────────

/** Drain all managed-contract pages up to the cap. */
async function drainManagedContracts(): Promise<ManagedContract[]> {
  const accumulated: ManagedContract[] = [];
  let nextToken: string | undefined;

  for (let page = 0; page < REGISTER_MAX_PAGES; page++) {
    const resp = await managedContractsApi.list({ limit: REGISTER_PAGE_SIZE, nextToken });
    for (const mc of resp.items) {
      accumulated.push(mc);
    }
    nextToken = resp.nextToken;
    if (!nextToken) break;
  }

  return accumulated;
}

/** Drain all draft-contract pages up to the cap. */
async function drainDraftContracts(): Promise<Contract[]> {
  const accumulated: Contract[] = [];
  let nextToken: string | undefined;

  for (let page = 0; page < DRAFTS_MAX_PAGES; page++) {
    const resp = await contractsApi.list({ limit: DRAFTS_PAGE_SIZE, nextToken });
    // ContractListResponse items are ContractListItem; cast to Contract for the
    // palette (the palette only needs id/title/referenceNumber/status/updatedAt).
    for (const draft of resp.items) {
      accumulated.push(draft as unknown as Contract);
    }
    nextToken = resp.nextToken;
    if (!nextToken) break;
  }

  return accumulated;
}

/** Drain all tracking items of one type for a contract up to the cap. */
async function drainRecords(
  contractId: string,
  type: (typeof TAB_LEVEL_TYPES)[number],
): Promise<TrackingItem[]> {
  const accumulated: TrackingItem[] = [];
  let nextToken: string | undefined;

  for (let page = 0; page < RECORDS_MAX_PAGES; page++) {
    const resp = await trackingApi.list(contractId, type, {
      limit: RECORDS_PAGE_SIZE,
      nextToken,
    });
    for (const item of resp.items) {
      accumulated.push(item);
    }
    nextToken = resp.nextToken;
    if (!nextToken) break;
  }

  return accumulated;
}

interface OrgRecordsDrainResult {
  items: TrackingItem[];
  /** True when the page cap was hit with more pages remaining. */
  truncated: boolean;
}

/** Drain org-wide tracking items of one type up to the cap. */
async function drainOrgRecords(
  type: (typeof TAB_LEVEL_TYPES)[number],
): Promise<OrgRecordsDrainResult> {
  const accumulated: TrackingItem[] = [];
  let nextToken: string | undefined;

  for (let page = 0; page < ORG_RECORDS_MAX_PAGES; page++) {
    const resp = await trackingApi.orgWideList(type, {
      limit: ORG_RECORDS_PAGE_SIZE,
      nextToken,
    });
    for (const item of resp.items) {
      accumulated.push(item);
    }
    nextToken = resp.nextToken;
    if (!nextToken) break;
  }

  return { items: accumulated, truncated: !!nextToken };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface PaletteSearchDataParams {
  /** Whether the palette is currently open. Drains are disabled when false. */
  open: boolean;
  /** Current palette query string. Record drains require length >= 1. */
  query: string;
  /** Current scope: 'global' or 'contract'. Record drains require 'contract'. */
  scope: "global" | "contract";
  /** Active contract ID when in contract scope, or null. */
  contractId: string | null;
}

export interface PaletteSearchDataResult {
  managedContracts: ManagedContract[];
  draftContracts: Contract[];
  records: TrackingItem[];
  /** Global scope only: the org-wide slim invoice listing (empty in contract
   *  scope — the finance tab has its own in-tab search there). */
  invoices: OrgWideInvoice[];
  /** Global scope only: true when any org drain was cap-truncated or failed.
   *  Drives the visible incomplete-search notice. Always false in contract scope. */
  recordsIncomplete: boolean;
  isLoading: boolean;
}

/**
 * Feeds the command palette with pre-drained domain data. All network requests
 * are React Query queries — deduped, cached, and not repeated mid-session
 * unless staleTime expires.
 */
export function usePaletteSearchData({
  open,
  query,
  scope,
  contractId,
}: PaletteSearchDataParams): PaletteSearchDataResult {
  const { hasArea } = useAuth();

  // Register drain — gated on open + manage area
  const registerQuery = useQuery<ManagedContract[]>({
    queryKey: ["palette", "managed-contracts"],
    queryFn: drainManagedContracts,
    enabled: open && hasArea("manage"),
    staleTime: PALETTE_STALE_TIME,
  });

  // Drafts drain — gated on open + generate area
  const draftsQuery = useQuery<Contract[]>({
    queryKey: ["palette", "draft-contracts"],
    queryFn: drainDraftContracts,
    enabled: open && hasArea("generate"),
    staleTime: PALETTE_STALE_TIME,
  });

  // Record drains (×8 parallel) — additionally gated on contract scope +
  // contractId + at least one typed character (first-keystroke rule).
  const recordsEnabled =
    open && scope === "contract" && !!contractId && query.trim().length >= 1;

  const recordQueries = useQueries({
    queries: TAB_LEVEL_TYPES.map((type) => ({
      queryKey: ["palette", "records", contractId, type],
      queryFn: () => drainRecords(contractId!, type),
      enabled: recordsEnabled,
      staleTime: PALETTE_STALE_TIME,
    })),
  });

  // Org-wide record drains (×8 parallel) — global-scope mirror of the record
  // drains: gated on manage area + first keystroke. Caps are wider than the
  // per-contract drains because they cover the whole register.
  const orgRecordsEnabled =
    open && scope === "global" && hasArea("manage") && query.trim().length >= 1;

  const orgRecordQueries = useQueries({
    queries: TAB_LEVEL_TYPES.map((type) => ({
      queryKey: ["palette", "org-records", type],
      queryFn: () => drainOrgRecords(type),
      enabled: orgRecordsEnabled,
      staleTime: PALETTE_STALE_TIME,
      // No retry: the app default (retry: 1) re-runs the WHOLE multi-page
      // drain from page 1, doubling the worst-case request budget. A failed
      // type flags recordsIncomplete instead; the next natural refetch retries.
      retry: false,
    })),
  });

  // Org-wide invoice drain — same gates as the org record drains. A single
  // request (the endpoint returns the whole slim listing, no pagination).
  const invoicesQuery = useQuery({
    queryKey: ["palette", "org-invoices"],
    queryFn: () => financeApi.listInvoicesOrgWide(),
    enabled: orgRecordsEnabled,
    staleTime: PALETTE_STALE_TIME,
    retry: false,
  });

  // Flatten is scope-exclusive: disabled React Query queries still expose
  // cached data, so results must only be read while their own enable gate
  // holds — both the scope AND the manage-area check. Without the area
  // check, a user whose manage access is revoked mid-session would keep
  // seeing cached record names and deep links.
  const records: TrackingItem[] = [];
  if (scope === "contract") {
    for (const q of recordQueries) {
      if (q.data) {
        for (const item of q.data) {
          records.push(item);
        }
      }
    }
  } else if (hasArea("manage")) {
    for (const q of orgRecordQueries) {
      // Skip errored drains even when stale cached data is present: an errored
      // type "couldn't be searched" (the notice says so), and after a server
      // 403 (e.g. manage access revoked) stale names must not linger.
      if (q.data && !q.isError) {
        for (const item of q.data.items) {
          records.push(item);
        }
      }
    }
  }

  // Same scope-exclusive rule for invoices: only surfaced in global scope
  // while the manage area holds, and never from an errored (stale-cache)
  // query.
  const invoices: OrgWideInvoice[] =
    scope === "global" && hasArea("manage") && invoicesQuery.data && !invoicesQuery.isError
      ? invoicesQuery.data.invoices
      : [];

  // Incomplete when any org drain (records or invoices) was cap-truncated or
  // settled in error — either way some rows were never searched and the UI
  // must say so. Gated like the flatten: no results shown means no notice
  // needed.
  const recordsIncomplete =
    scope === "global" &&
    hasArea("manage") &&
    (orgRecordQueries.some((q) => q.isError || q.data?.truncated === true) ||
      invoicesQuery.isError);

  const isLoading =
    (registerQuery.isFetching && hasArea("manage") && open) ||
    (draftsQuery.isFetching && hasArea("generate") && open) ||
    (recordsEnabled && recordQueries.some((q) => q.isFetching)) ||
    (orgRecordsEnabled && (orgRecordQueries.some((q) => q.isFetching) || invoicesQuery.isFetching));

  return {
    managedContracts: registerQuery.data ?? [],
    draftContracts: draftsQuery.data ?? [],
    records,
    invoices,
    recordsIncomplete,
    isLoading,
  };
}
